// 在地化（i18n）引擎 — 對應 Unity Localization（String Table + 依語系字型）。
//
// 13 語系（en, zh-Hans, zh-Hant, ja-JP, ko-KR, th, vi, id, my-MM, bn, pt, es-ES, tr-TR，已驗證自來源）。
// 語系選擇（完全對齊 Unity URLParameter／StartupLocaleSelector，見 CLAUDE.md 2026-07-29 條目）：
//   網址 `?l=` 優先、其次 `?lang=`，小寫前綴比對正規化；無參數即固定專案預設語系 en，
//   不讀瀏覽器語言、不記憶（Unity 玩家端亦無 in-game 切換，語系於開機一次定案）。
// 字型策略：依語系挑選涵蓋該字集的 web 字型堆疊（CJK／泰文／緬甸文／孟加拉文另切，見 fontFamilyForLocale）。
//
// t(key) 查詢順序：當前語系 → en 後備 → key 本身（缺翻譯時不致崩壞，並讓缺漏可見）。
// 翻譯字典見 messages.ts；目前涵蓋主要 UI 字串，其餘語系缺漏時回退 en（完整 13 語系字串可再由 Unity
// Data/Localization 匯出補齊，屬機械性擴充）。

import { MESSAGES } from './messages'
import { UNITY_STRINGS } from './unityStrings.data'

export const SUPPORTED_LOCALES = [
  'en',
  'zh-Hans',
  'zh-Hant',
  'ja-JP',
  'ko-KR',
  'th',
  'vi',
  'id',
  'my-MM',
  'bn',
  'pt',
  'es-ES',
  'tr-TR',
] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

// 對應 Unity LocalizationSettings 的 m_ProjectLocaleIdentifier（m_Code: en）——無網址參數時的預設語系。
export const DEFAULT_LOCALE: Locale = 'en'
const FALLBACK_LOCALE: Locale = 'en'

/** 依語系挑選 web 字型堆疊（涵蓋該字集）。作為 Unity 原生字型載入失敗時的系統字型 fallback。 */
const FONT_STACKS: Partial<Record<Locale, string>> = {
  'zh-Hans': "'Noto Sans CJK SC', 'Microsoft YaHei', 'PingFang SC', sans-serif",
  'zh-Hant': "'Noto Sans CJK TC', 'Microsoft JhengHei', 'PingFang TC', sans-serif",
  'ja-JP': "'Noto Sans CJK JP', 'Hiragino Sans', 'Yu Gothic', sans-serif",
  'ko-KR': "'Noto Sans CJK KR', 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif",
  th: "'Noto Sans Thai', 'Leelawadee UI', sans-serif",
  'my-MM': "'Noto Sans Myanmar', 'Padauk', 'Myanmar Text', sans-serif",
  bn: "'Noto Sans Bengali', 'Vrinda', sans-serif",
}
const DEFAULT_FONT_STACK = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif"

/**
 * Unity 原生字型（Font_AssetTable 每語系對應的 TMP 字型底層 ttf，已自來源專案複製至 public/fonts）：
 * 拉丁 6 語系共用 LiberationSans；zh-Hans/zh-Hant 共用 AlibabaPuHuiTi；ja/ko 用 NotoSans JP/KR；
 * 泰/緬/孟用 NotoSans*-Adjusted（字距調整版）。開機時只載入當前語系那一支（loadLocaleFont）。
 */
const LOCALE_FONTS: Record<Locale, { readonly family: string; readonly url: string }> = {
  en: { family: 'LiberationSans', url: '/fonts/LiberationSans.ttf' },
  vi: { family: 'LiberationSans', url: '/fonts/LiberationSans.ttf' },
  id: { family: 'LiberationSans', url: '/fonts/LiberationSans.ttf' },
  pt: { family: 'LiberationSans', url: '/fonts/LiberationSans.ttf' },
  'es-ES': { family: 'LiberationSans', url: '/fonts/LiberationSans.ttf' },
  'tr-TR': { family: 'LiberationSans', url: '/fonts/LiberationSans.ttf' },
  'zh-Hans': { family: 'AlibabaPuHuiTi', url: '/fonts/AlibabaPuHuiTi-3-55-Regular.ttf' },
  'zh-Hant': { family: 'AlibabaPuHuiTi', url: '/fonts/AlibabaPuHuiTi-3-55-Regular.ttf' },
  'ja-JP': { family: 'NotoSansJP', url: '/fonts/NotoSansJP-Regular.ttf' },
  'ko-KR': { family: 'NotoSansKR', url: '/fonts/NotoSansKR-Regular.ttf' },
  th: { family: 'NotoSansThai', url: '/fonts/NotoSansThai-Regular-Adjusted.ttf' },
  'my-MM': { family: 'NotoSansMyanmar', url: '/fonts/NotoSansMyanmar-Regular-Adjusted.ttf' },
  bn: { family: 'NotoSansBengali', url: '/fonts/NotoSansBengali-Regular-Adjusted.ttf' },
}

let localeFontReady = false

/**
 * 原始 ttf → 子集 woff2 的路徑對應（命名規則須與 scripts/subset-fonts.mjs 的輸出一致）。
 * 子集只含遊戲會顯示的字（約 1900 碼位），開機那支從 8.14MB 降到約 0.14MB。
 */
function subsetUrlOf(url: string): string {
  const name = url.slice(url.lastIndexOf('/') + 1).replace(/\.(ttf|otf)$/i, '')
  return `/fonts/subset/${name}.woff2`
}

/**
 * 載入當前語系的 Unity 原生字型（FontFace API）。於開機資源載入階段呼叫一次。
 *
 * 依序嘗試：①子集 woff2（小、預設走這條）②原始完整 ttf（子集檔沒產生時的退路）
 * ③都失敗就回退系統字型堆疊（fontFamilyForLocale 會自動反映）。
 * ⚠️ 子集若漏字不會走到 ②——瀏覽器是逐字回退的，缺的字會用系統字型畫出來，不會變豆腐方塊。
 */
export async function loadLocaleFont(locale: Locale = currentLocale): Promise<void> {
  const def = LOCALE_FONTS[locale]
  if (typeof FontFace === 'undefined' || typeof document === 'undefined') return
  for (const url of [subsetUrlOf(def.url), def.url]) {
    try {
      const face = new FontFace(def.family, `url(${url})`)
      await face.load()
      document.fonts.add(face)
      localeFontReady = true
      return
    } catch {
      // 換下一個候選；全部失敗才落到系統字型堆疊。
    }
  }
  localeFontReady = false
}

function isSupported(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/** 將 BCP-47 語言標籤正規化到支援清單（如 zh-TW→zh-Hant、zh-CN→zh-Hans、ja→ja-JP）。無法對應回 null。 */
function normalizeLocale(tag: string): Locale | null {
  const lower = tag.toLowerCase()
  if (isSupported(tag)) return tag
  if (lower.startsWith('zh')) {
    // Unity StartupLocaleSelector：zh-tw／zh-cht／zh-hant／zh-hk → 繁中，其餘 zh* → 簡中。
    if (lower.includes('hant') || lower.includes('cht') || lower.includes('tw') || lower.includes('hk')) return 'zh-Hant'
    return 'zh-Hans'
  }
  const prefixMap: Record<string, Locale> = {
    en: 'en',
    ja: 'ja-JP',
    ko: 'ko-KR',
    th: 'th',
    vi: 'vi',
    id: 'id',
    my: 'my-MM',
    bn: 'bn',
    pt: 'pt',
    es: 'es-ES',
    tr: 'tr-TR',
  }
  const prefix = lower.split('-')[0]!
  return prefixMap[prefix] ?? null
}

let currentLocale: Locale = DEFAULT_LOCALE
const listeners = new Set<() => void>()

/** 目前語系。 */
export function getLocale(): Locale {
  return currentLocale
}

/** 切換語系並通知訂閱者（供 UI 重新以新語系/字型佈局）。未支援的語系忽略。 */
export function setLocale(locale: Locale): void {
  if (!isSupported(locale) || locale === currentLocale) return
  currentLocale = locale
  for (const listener of listeners) listener()
}

/** 訂閱語系變更，回傳取消訂閱函式。 */
export function onLocaleChange(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/**
 * 偵測初始語系，完全對齊 Unity URLParameter／StartupLocaleSelector：
 *   網址 `?l=` 優先、其次 `?lang=`（小寫前綴比對正規化）；皆無或無法對應時回 DEFAULT_LOCALE（en）。
 * ⚠️ 刻意不讀瀏覽器語言、不記憶——Unity 無參數即固定專案預設語系（見 CLAUDE.md 2026-07-29 條目）。
 * search 參數可注入以利測試（預設讀 window.location.search）。
 */
export function detectLocale(search?: string): Locale {
  const query = search ?? (typeof window !== 'undefined' ? window.location.search : '')
  const params = new URLSearchParams(query)
  const param = params.get('l') ?? params.get('lang')
  if (param !== null) {
    const normalized = normalizeLocale(param)
    if (normalized !== null) return normalized
  }
  return DEFAULT_LOCALE
}

/**
 * 依語系回傳字型堆疊：Unity 原生字型（loadLocaleFont 成功後）優先，其次系統字型堆疊。
 * 對應 Unity Font_AssetTable 的每語系獨立字型。
 */
export function fontFamilyForLocale(locale: Locale = currentLocale): string {
  const stack = FONT_STACKS[locale] ?? DEFAULT_FONT_STACK
  return localeFontReady ? `'${LOCALE_FONTS[locale].family}', ${stack}` : stack
}

/** 移除 Unity TMP 富文字標籤（如 `<color=yellow>`、`</color>`、`<b>`），PixiJS Text 無法解析。
 *  只比對「< 後緊接字母或 /」的標籤樣式，不會誤刪數學式 `a < b` 或 `<3`。 */
function stripRichText(text: string): string {
  return text.replace(/<\/?[a-zA-Z][^>]*>/g, '')
}

/**
 * 翻譯查詢：當前語系 → en 後備 → key 本身。params 以 `{name}`／`{0}` 佔位符插值。
 * 字典來源：手寫 MESSAGES 優先，其次由 Unity Localization 匯出的 UNITY_STRINGS（game./common./dialog.）。
 * 對應 Unity LocalizationManager 的 GetLocalizedString（含 Smart String 的 `{0}` 位置參數）。
 */
export function t(key: string, params?: Readonly<Record<string, string | number>>): string {
  const entry = MESSAGES[key] ?? UNITY_STRINGS[key]
  const template = stripRichText(entry?.[currentLocale] ?? entry?.[FALLBACK_LOCALE] ?? key)
  if (params === undefined) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name]
    return value === undefined ? match : String(value)
  })
}
